// What counts as a reward — the one rule, read by every surface that lets an
// operator write one.
//
// Two of them do today: a leaderboard reward's `payload`
// (features/leaderboards/rewardPayload.ts) and a help request type's
// `donor_reward` (features/projects/config/helpTypes.ts). Both are arbitrary
// JSON no Go path ever reads its keys of, and a game models what it hands over
// either as a single object ({"gold":250,"gems":10}) or as a list of items
// ([{"currency":"Coins","amount":600}, …]), so both shapes are accepted at the
// top level.
//
// A bare scalar (600, "gems", true, null) is not: a reward's contents can't be
// a lone value, so that is a half-typed reward rather than an intent, and
// saying so early beats storing it. The help server reads its config side by
// exactly this rule (internal/help/config.go, parseDonorReward), where a
// scalar costs the type its reward.
//
// The rule lives here rather than in each editor because it used to live in
// each editor, and that is precisely how the leaderboards' two of them drifted
// apart from the backend. Two surfaces may word a refusal differently — one
// calls the thing a payload, the other a reward — but they may not disagree
// about what a reward IS, which is why the wording is a separate function over
// the same verdict.

/** Why a written reward was refused. */
export type RewardProblem = "invalid_json" | "not_a_container";

/** Outcome of reading a reward out of a text box. `value` is `undefined` when
 *  the box is blank; each caller decides whether that goes on the wire as an
 *  omitted key or as `{}`. */
export type ParsedReward =
  | { ok: true; value: unknown }
  | { ok: false; problem: RewardProblem };

export function parseReward(raw: string): ParsedReward {
  const trimmed = raw.trim();
  // Blank means "no reward" — a state, not a mistake.
  if (trimmed.length === 0) return { ok: true, value: undefined };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, problem: "invalid_json" };
  }

  // typeof null === "object", and null is one of the scalars we turn away.
  if (parsed === null || typeof parsed !== "object") {
    return { ok: false, problem: "not_a_container" };
  }
  return { ok: true, value: parsed };
}

/** The same rule applied to a value that is already parsed — what a config
 *  loaded from the server holds, rather than what an operator typed. */
export function isReward(value: unknown): boolean {
  return value !== null && typeof value === "object";
}

/**
 * Whether the text in a reward box still MEANS what the config under it holds.
 *
 * Every reward editor keeps the operator's own text while they type it — reading
 * it back off the parsed value would reformat the line under their cursor — and
 * this is what decides when that text stops being theirs to keep: a Discard, an
 * edit through the Raw JSON tab or a row removed above it all replace the value,
 * and the answer goes false the moment the two say different things. Spacing is
 * not a difference; that is the whole point of keeping the text.
 */
export function sameReward(text: string, reward: unknown): boolean {
  const parsed = parseReward(text);
  // Text that is not a reward yet lives in the config verbatim, so the stored
  // value itself is what it has to match.
  if (!parsed.ok) return text === reward;
  return JSON.stringify(parsed.value) === JSON.stringify(reward);
}

/**
 * A reward as a one-line box reads it out — a help type's donor reward, and
 * every reward cell of the Invites section.
 *
 * A reward the operator is still typing is already their own text and stays
 * exactly as they left it — that is what a string in the config is for.
 *
 * A stored one is printed on one line but SPACED — `{"coins": 50}`, never
 * `{"coins":50}` — which is how the mockups show it and how a person writes one
 * by hand. `JSON.stringify` has no such mode, so the indenter is used and its
 * own line breaks are unwrapped: a line break inside a string value comes out
 * of it escaped (`\n`, two characters), so a real newline in this output can
 * only be the indenter's, and there is nothing else for the two replacements to
 * reach.
 */
export function rewardToLine(reward: unknown): string {
  if (reward === undefined) return "";
  if (typeof reward === "string") return reward;
  // Everything else in a reward key came out of JSON — a stored value or one an
  // editor parsed — so there is nothing here that cannot be printed.
  return JSON.stringify(reward, null, 1)
    .replace(/,\n\s*/g, ", ")
    .replace(/\n\s*/g, "");
}

/**
 * The refusal in words, with the surface's own noun in front of it — "Payload"
 * where the field is called a payload, "Reward" where it is called a reward. A
 * message that named the other surface's field would read as being about
 * something else entirely.
 */
export function rewardProblemText(
  problem: RewardProblem,
  noun: string,
): string {
  return problem === "invalid_json"
    ? `${noun} must be valid JSON.`
    : `${noun} must be a JSON object or an array of items — a single value like 600 can't be a reward's contents.`;
}
